import { boatServiceApi } from '@/shared/api/boat-service.api';
import yachtImage from '@/shared/assets/images/yacht.png';
import { AppStoreTag } from '@/shared/constants/app-store-tag.constant';
import { OptionList } from '@/shared/lib/interfaces/option.interface';
import { Pagination } from '@/shared/lib/interfaces/pagination.interface';

import { transformBoatResponse } from '../model/helpers/transform-boat-response.helper';
import { boatActions } from '../model/slice/boat.slice';
import { Boat, BoatSearch } from '../model/types/boat.interface';
import {
	BoatApiVariables,
	BoatResponse,
	BoatsApiVariables,
	ConnectedUsertoYachtVariables,
	GetBoatsSearchResponse,
} from '../model/types/boat-api.interface';

const boatApi = boatServiceApi.injectEndpoints({
	endpoints: (build) => ({
		getBoats: build.query<Pagination<Boat>, BoatsApiVariables>({
			providesTags: [AppStoreTag.Boat],
			query: ({ value, page, size }) => {
				const params = new URLSearchParams({
					page: String(page),
					size: String(size),
				});

				if (value) params.set('search', value);

				return {
					method: 'GET',
					url: `boat/?${params.toString()}`,
				};
			},
			transformResponse: (response: Pagination<BoatResponse>, _, { timezone }): Pagination<Boat> => ({
				...response,
				items: response.items.map((data) => transformBoatResponse(data, timezone)),
			}),
		}),
		getBoat: build.query<Boat, BoatApiVariables>({
			providesTags: [AppStoreTag.BoatDetail],
			query: ({ id }) => ({
				method: 'GET',
				url: `boat/${id}/`,
			}),
			transformResponse: (response: BoatResponse, _, { timezone }): Boat => transformBoatResponse(response, timezone),
			async onQueryStarted(variables, { queryFulfilled, dispatch }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(boatActions.updateBoat(data));
				} catch {}
			},
		}),
		connectedUsersToYacht: build.query<OptionList, ConnectedUsertoYachtVariables>({
			query: ({ boatId }) => ({
				method: 'GET',
				url: `boat/${boatId}/`,
			}),
			transformResponse: (response: BoatResponse, _, { currentUserId }): OptionList =>
				(response.access_users ?? [])
					// Grafana provisioning is keyed on the legacy id, so users that only exist in
					// Keycloak cannot be copy targets yet.
					.filter(({ legacy_user_id: legacyUserId }) => legacyUserId !== null)
					.filter(({ legacy_user_id: legacyUserId }) => String(legacyUserId) !== currentUserId)
					.map((user) => ({
						value: String(user.legacy_user_id),
						label: user.username,
					})),
		}),
		getUserBoats: build.query<BoatSearch[], string>({
			query: () => ({
				method: 'GET',
				url: 'boat/me',
			}),
			transformResponse: (response: GetBoatsSearchResponse[]): BoatSearch[] =>
				response.map((data) => ({
					id: String(data.id),
					mmsi: data.mmsi,
					name: data.name,
					uuid: data.boat_uuid,
					imageUrl: data?.image_url || yachtImage,
				})),
			async onQueryStarted(variables, { queryFulfilled, dispatch }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(boatActions.setBoats(data));
					if (data.length === 1) {
						dispatch(boatActions.setBoatById(data[0].id));
					}
				} catch {}
			},
		}),
		getBoatsByUser: build.query<BoatSearch[], string>({
			providesTags: (_result, _error, userId) =>
				userId ? [{ type: AppStoreTag.BoatByUser, id: userId }] : [{ type: AppStoreTag.BoatByUser }],
			query: (id) => ({
				method: 'GET',
				url: `boat/users/${id}/boats`,
			}),
			transformResponse: (response: GetBoatsSearchResponse[]): BoatSearch[] =>
				response.map((data) => ({
					id: String(data.id),
					mmsi: data.mmsi,
					name: data.name,
					uuid: data.boat_uuid,
					imageUrl: data?.image_url || yachtImage,
				})),
		}),
	}),
});

export const {
	useGetBoatQuery,
	useGetUserBoatsQuery,
	useGetBoatsByUserQuery,
	useGetBoatsQuery,
	useConnectedUsersToYachtQuery,
} = boatApi;
